import { ChevronDown, Share2 } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { buildJobShareMenuItems, buildJobShareUrl } from "../lib/jobShareLinks";
import SharePlatformIcon from "./SharePlatformIcon";

/**
 * Share dropdown for job detail header — uses canonical SEO URL for all channels.
 */
export default function JobShareMenu({ job, className = "" }) {
  const menuId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [copyNotice, setCopyNotice] = useState("");

  const shareUrl = buildJobShareUrl(job);
  const items = buildJobShareMenuItems(job);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeMenu]);

  useEffect(() => {
    if (!copyNotice) return undefined;
    const timer = window.setTimeout(() => setCopyNotice(""), 2400);
    return () => window.clearTimeout(timer);
  }, [copyNotice]);

  const handleItemClick = async (item) => {
    if (item.action === "copy") {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopyNotice("Link copied");
      } catch {
        setCopyNotice("We couldn't copy that link. Try selecting it from the address bar.");
      }
      closeMenu();
      return;
    }
    if (item.href) {
      window.open(item.href, "_blank", "noopener,noreferrer");
      closeMenu();
    }
  };

  return (
    <div
      ref={rootRef}
      className={`nd1-job-share-menu${className ? ` ${className}` : ""}${open ? " nd1-job-share-menu--open" : ""}`}
    >
      <button
        type="button"
        className="nd1-job-share-menu__trigger nd1-btn nd1-btn--outline"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Share2 className="nd1-icon nd1-job-share-menu__trigger-icon" size={18} strokeWidth={1.75} aria-hidden="true" />
        <span>Share</span>
        <ChevronDown className="nd1-icon nd1-job-share-menu__chevron" size={16} strokeWidth={1.75} aria-hidden="true" />
      </button>

      {open ? (
        <div id={menuId} className="nd1-job-share-menu__panel" role="menu">
          {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="nd1-job-share-menu__item"
                role="menuitem"
                onClick={() => void handleItemClick(item)}
              >
                <SharePlatformIcon platform={item.id} />
                <span>{item.label}</span>
              </button>
            ))}
        </div>
      ) : null}

      {copyNotice ? (
        <p className="nd1-job-share-menu__notice" role="status">
          {copyNotice}
        </p>
      ) : null}
    </div>
  );
}
